/**
 * CredentialField
 *
 * Reusable masked credential block. Displays last-4 masking when configured;
 * shows an input form otherwise. API calls are fully injectable so this
 * component can be used anywhere in the app regardless of endpoint shape.
 *
 * Props:
 *  - title    {string}   — section heading shown above the block
 *  - fields   {Array<{key: string, label: string, placeholder: string, type?: string}>}
 *             — input fields to collect; drives both the entry form and display.
 *             If onFetch returns credential_key, the first field label is used
 *             to display it un-masked. Subsequent fields are shown as ••••{last4}.
 *  - onFetch  {async () => { configured, credential_key?, last4? }}
 *  - onSave   {async (form) => { configured, credential_key?, last4? }}
 *  - onRevoke {async () => void}
 */

import { useCallback, useEffect, useState } from "react";
import Input from "../Inputs/Input";
import { PrimaryButton, CompactButton } from "../Button/Buttons";
import useFeedback from "../../hooks/useFeedback";

const emptyForm = (fields) => Object.fromEntries(fields.map((f) => [f.key, ""]));

const TEXTAREA_BASE =
  "bg-fill-hex-fafbfe dark:bg-fill-navigation-surface-dark block w-full p-[10px] text-[13px] font-mono text-ink-ink dark:text-ink-white placeholder-ink-copy-hover outline-none border-[0.5px] border-edge-hex-aaaaaa dark:border-edge-hex-aaaaaa80 rounded-[10px] placeholder:italic resize-y min-h-[110px] leading-[1.5]";

const CredentialField = ({ title, description, fields, onFetch, onSave, onRevoke, onRotate, rotateSuccessMessage }) => {
  const { showFeedback } = useFeedback();
  const [status, setStatus]     = useState({ configured: false, credential_key: "", last4: "" });
  const [form, setForm]         = useState(() => emptyForm(fields));
  const [saving, setSaving]     = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [rotating, setRotating] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await onFetch();
      setStatus({
        configured:     data.configured,
        credential_key: data.credential_key ?? "",
        last4:          data.last4          ?? "",
      });
    } catch {
      // not yet configured — defaults are fine
    }
  }, [onFetch]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await onSave(form);
      setStatus({
        configured:     result.configured,
        credential_key: result.credential_key ?? "",
        last4:          result.last4          ?? "",
      });
      setForm(emptyForm(fields));
      showFeedback("Credentials saved.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save credentials.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await onRevoke();
      setStatus({ configured: false, credential_key: "", last4: "" });
      showFeedback("Credentials revoked.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to revoke credentials.", "error");
    } finally {
      setRevoking(false);
    }
  };

  const handleRotate = async () => {
    if (!onRotate) {
      setStatus((s) => ({ ...s, configured: false }));
      return;
    }
    setRotating(true);
    try {
      const result = await onRotate();
      // Pre-fill any form fields whose key appears in the result (e.g. private_key_pem)
      if (result) {
        const prefill = {};
        fields.forEach((f) => { if (result[f.key] !== undefined) prefill[f.key] = result[f.key]; });
        if (Object.keys(prefill).length > 0) setForm((f) => ({ ...f, ...prefill }));
      }
      // Show the input form so the user can review the pre-filled key before saving
      setStatus({ configured: false, credential_key: "", last4: "" });
      showFeedback(rotateSuccessMessage ?? "New keypair generated — review and click Save.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to generate keypair.", "error");
    } finally {
      setRotating(false);
    }
  };

  const canSave = fields.every((f) => form[f.key]?.trim());

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 p-4">
      <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">{title}</p>
      {description && (
        <div className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500 -mt-1">{description}</div>
      )}

      {status.configured ? (
        <div className="flex flex-col gap-1.5">
          {status.credential_key && (
            <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
              {fields[0]?.label}:{" "}
              <span className="font-mono text-ink-gray-700 dark:text-ink-gray-300">
                {status.credential_key}
              </span>
            </span>
          )}
          {status.last4 && (
            <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
              {fields.length > 1 ? fields[fields.length - 1]?.label : "Token"}:{" "}
              <span className="font-mono text-ink-gray-700 dark:text-ink-gray-300">
                ••••{status.last4}
              </span>
            </span>
          )}
          {!status.credential_key && !status.last4 && (
            <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">Configured</span>
          )}
          <div className="flex gap-2 mt-2">
            <CompactButton
              onClick={handleRotate}
              disabled={rotating}
              className="px-4 py-1.5"
            >
              {rotating ? "Rotating…" : "Rotate"}
            </CompactButton>
            <CompactButton
              onClick={handleRevoke}
              disabled={revoking}
              className="px-4 py-1.5 border-edge-red-500 text-ink-red-500 hover:bg-fill-red-500/5"
            >
              {revoking ? "Revoking…" : "Revoke"}
            </CompactButton>
          </div>
        </div>
      ) : (
        <>
          {fields.map((field) => (
            <div key={field.key} className="flex flex-col gap-1.5">
              <label className="text-xs text-ink-gray-700 dark:text-ink-gray-300">{field.label}</label>
              {field.multiline ? (
                <textarea
                  className={TEXTAREA_BASE}
                  placeholder={field.placeholder ?? ""}
                  value={form[field.key]}
                  onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.value }))}
                  spellCheck={false}
                  autoComplete="off"
                />
              ) : (
                <Input
                  type={field.type ?? "text"}
                  placeholder={field.placeholder ?? ""}
                  value={form[field.key]}
                  onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.value }))}
                />
              )}
              {field.hint && (
                <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">{field.hint}</p>
              )}
            </div>
          ))}
          <div>
            <PrimaryButton onClick={handleSave} disabled={saving || !canSave}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </>
      )}
    </div>
  );
};

export default CredentialField;
